/**
 * Bulk assistance booking upload — file parsing, normalisation and validation.
 *
 * Used by the "Bulk Upload" option on the Create Request screen. Parsing happens
 * entirely in the browser (xlsx is already a dependency) so the operator sees every
 * problem before a single request is sent to the API.
 *
 * Accepted columns (header row required, order and casing irrelevant):
 *   First Name, Last Name, Assistance Requirements, Confirmed with Airline,
 *   Assistance at Origin Airport, Assistance at Destination Airport,
 *   Carrier Code, Flight Number, Flight Date, Flight Direction,
 *   Reference Number (optional)
 */

import * as XLSX from 'xlsx';
import { PRMCodes } from './constants';
import { getPRMCodeOptions } from './codes';
import { sanitizeSpreadsheetCell } from './sanitize-spreadsheet-cell';

// ─── configuration ────────────────────────────────────────────────────────────

export const MAX_BULK_ROWS = 20;

export const BULK_TEMPLATE_COLUMNS = [
    'First Name',
    'Last Name',
    'Assistance Requirements',
    'Confirmed with Airline',
    'Assistance at Origin Airport',
    'Assistance at Destination Airport',
    'Carrier Code',
    'Flight Number',
    'Flight Date',
    'Flight Direction',
    'Reference Number',
];

// Every header spelling we accept, normalised (lowercase, alphanumeric only) → field key.
const HEADER_ALIASES = {
    firstname: 'firstName',
    forename: 'firstName',
    givenname: 'firstName',
    lastname: 'secondName',
    secondname: 'secondName',
    surname: 'secondName',
    familyname: 'secondName',
    assistancerequirements: 'assistance',
    assistance: 'assistance',
    assistancerequired: 'assistance',
    prmcode: 'assistance',
    prmcodes: 'assistance',
    specialassistance: 'assistance',
    confirmedwithairline: 'airlineConfirmed',
    airlineconfirmed: 'airlineConfirmed',
    confirmedwithcarrier: 'airlineConfirmed',
    assistanceatoriginairport: 'originAssistance',
    originassistance: 'originAssistance',
    assistanceatorigin: 'originAssistance',
    assistanceatdestinationairport: 'destinationAssistance',
    destinationassistance: 'destinationAssistance',
    assistanceatdestination: 'destinationAssistance',
    carriercode: 'carrierCode',
    airlinecode: 'carrierCode',
    airline: 'carrierCode',
    flightnumber: 'flightNumber',
    flightno: 'flightNumber',
    flightnum: 'flightNumber',
    flightdate: 'flightDate',
    date: 'flightDate',
    dateofflight: 'flightDate',
    flightdirection: 'flightType',
    direction: 'flightType',
    flighttype: 'flightType',
    referencenumber: 'referenceNumber',
    reference: 'referenceNumber',
    bookingreference: 'referenceNumber',
    pnr: 'referenceNumber',
};

const REQUIRED_FIELDS = [
    'firstName',
    'secondName',
    'assistance',
    'carrierCode',
    'flightNumber',
    'flightDate',
    'flightType',
];

// Field key → the column heading we tell the operator to use.
const FIELD_LABELS = {
    firstName: 'First Name',
    secondName: 'Last Name',
    assistance: 'Assistance Requirements',
    airlineConfirmed: 'Confirmed with Airline',
    originAssistance: 'Assistance at Origin Airport',
    destinationAssistance: 'Assistance at Destination Airport',
    carrierCode: 'Carrier Code',
    flightNumber: 'Flight Number',
    flightDate: 'Flight Date',
    flightType: 'Flight Direction',
    referenceNumber: 'Reference Number',
};

const CARRIER_CODE_REGEX = /^[A-Za-z0-9]{2}[A-Za-z]?$/;
const FLIGHT_NUMBER_REGEX = /^\d{1,4}[A-Za-z]?$/;

// Marks the template's example row. Anything in the first column starting with this
// is guidance, not a passenger, and is dropped before validation.
const EXAMPLE_ROW_MARKER = 'example';

// Values that appear in published template example rows and can never be real
// passenger data. Two or more in the same row means it is the example, not a booking.
const TEMPLATE_PLACEHOLDERS = [
    'y/n', 'ei/fr/ek etc.', 'ei/fr/ek etc', 'e. g. 1234', 'e.g. 1234',
    'dd/mm/yyy', 'dd/mm/yyyy', 'inbound/outbound',
];

// ─── small helpers ────────────────────────────────────────────────────────────

const normaliseHeader = (header) => String(header || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const clean = (value) => {
    if (value === null || value === undefined) return '';
    if (value instanceof Date) return value;
    return String(value).trim();
};

const cleanName = (value) => String(value || '').trim().replace(/\s+/g, ' ');

/**
 * Y / Yes / TRUE / 1 → true.  N / No / FALSE / 0 → false.  Blank → fallback.
 * Anything else → null so the row can be rejected with a clear message.
 */
const parseBoolean = (value, fallback) => {
    const text = String(value ?? '').trim().toLowerCase();
    if (!text) return fallback;
    if (['y', 'yes', 'true', '1', 'confirmed'].includes(text)) return true;
    if (['n', 'no', 'false', '0'].includes(text)) return false;
    return null;
};

const pad = (n) => String(n).padStart(2, '0');
const toISO = (year, month, day) => `${year}-${pad(month)}-${pad(day)}`;

/**
 * Accepts dd/mm/yyyy (the documented format), dd-mm-yyyy, yyyy-mm-dd, a JS Date
 * (xlsx gives one for real date cells) and an Excel serial number.
 * Returns yyyy-mm-dd or null.
 */
export const parseFlightDate = (value) => {
    if (value === null || value === undefined || value === '') return null;

    if (value instanceof Date && !isNaN(value.getTime())) {
        return toISO(value.getFullYear(), value.getMonth() + 1, value.getDate());
    }

    // Excel serial date
    if (typeof value === 'number' && isFinite(value)) {
        const parsed = XLSX.SSF && XLSX.SSF.parse_date_code ? XLSX.SSF.parse_date_code(value) : null;
        if (parsed && parsed.y) return toISO(parsed.y, parsed.m, parsed.d);
        return null;
    }

    const text = String(value).trim();

    // yyyy-mm-dd / yyyy.mm.dd
    let match = text.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
    if (match) {
        const [, y, m, d] = match.map(Number);
        return isValidDate(y, m, d) ? toISO(y, m, d) : null;
    }

    // dd/mm/yyyy, dd-mm-yyyy, dd.mm.yy
    match = text.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/);
    if (match) {
        const day = Number(match[1]);
        const month = Number(match[2]);
        let year = Number(match[3]);
        if (year < 100) year += 2000;
        return isValidDate(year, month, day) ? toISO(year, month, day) : null;
    }

    return null;
};

const isValidDate = (year, month, day) => {
    if (!year || !month || !day) return false;
    if (month < 1 || month > 12 || day < 1 || day > 31) return false;
    const d = new Date(year, month - 1, day);
    return d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day;
};

const parseDirection = (value) => {
    const text = String(value || '').trim().toLowerCase();
    if (!text) return null;
    if (['inbound', 'in', 'arrival', 'arriving', 'arr', 'i'].includes(text)) return 'INBOUND';
    if (['outbound', 'out', 'departure', 'departing', 'dep', 'o'].includes(text)) return 'OUTBOUND';
    return null;
};

/** Split "WCHR, BLND" / "WCHR; BLND" / "WCHR BLND" into upper-cased codes. */
const parseAssistance = (value) =>
    String(value || '')
        .split(/[,;/|\s]+/)
        .map((code) => code.trim().toUpperCase())
        .filter(Boolean);

const isPlaceholderRow = (row) => {
    // Our own template marks its example row explicitly.
    const firstName = String(row.firstName ?? '').trim().toLowerCase();
    if (firstName.startsWith(EXAMPLE_ROW_MARKER) || firstName.startsWith('#')) return true;

    const values = Object.values(row)
        .map((v) => String(v ?? '').trim().toLowerCase())
        .filter(Boolean);
    if (values.length === 0) return true;   // blank row
    const placeholders = values.filter((v) => TEMPLATE_PLACEHOLDERS.includes(v));
    return placeholders.length >= 2;
};

/** Carrier codes the API expects under a different IATA designator. */
const normaliseCarrierCode = (code) => {
    const cc = String(code || '').trim().toUpperCase();
    if (cc === 'EZY' || cc === 'EJU') return 'U2';
    return cc;
};

export const flightKeyOf = (row) =>
    `${row.carrierCode}${row.flightNumber}|${row.flightDate}|${row.flightType}`;

export const flightLabelOf = (row) =>
    `${row.carrierCode}${row.flightNumber} · ${row.flightDate} · ${row.flightType === 'INBOUND' ? 'Inbound' : 'Outbound'}`;

// ─── file reading ────────────────────────────────────────────────────────────

/**
 * Turn a worksheet into rows keyed by our internal field names.
 *
 * Works from the raw cell matrix rather than sheet_to_json's object form so that
 * blank and duplicated headings cannot silently collide, and so that data pasted
 * without a heading row can still be read using the documented column order.
 */
const interpretSheet = (sheet) => {
    // raw values, NOT the formatted display string. A date cell formatted m/d/yy would
    // otherwise arrive as "9/5/26" and be read as 9 May instead of 5 September — a
    // silently wrong flight date. Raw gives a real Date object, which is unambiguous.
    const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', blankrows: false });

    if (matrix.length === 0) {
        throw new Error('No data was found.');
    }

    const firstRow = (matrix[0] || []).map((cell) => String(cell ?? '').trim());
    const recognised = firstRow.filter((cell) => HEADER_ALIASES[normaliseHeader(cell)]).length;

    // Three or more recognisable headings means row 1 is a heading row. Otherwise we
    // assume the operator pasted data rows only and fall back to the template order.
    const hasHeaderRow = recognised >= 3;

    const headerCells = hasHeaderRow ? firstRow : BULK_TEMPLATE_COLUMNS;
    const dataRows = hasHeaderRow ? matrix.slice(1) : matrix;
    const firstDataSheetRow = hasHeaderRow ? 2 : 1;

    const fieldByColumn = [];
    const unknownHeaders = [];
    headerCells.forEach((cell, columnIndex) => {
        const field = HEADER_ALIASES[normaliseHeader(cell)];
        // A field named twice keeps its first column; later duplicates are ignored.
        if (field && !fieldByColumn.includes(field)) fieldByColumn[columnIndex] = field;
        else if (String(cell).trim()) unknownHeaders.push(String(cell).trim());
    });

    if (hasHeaderRow) {
        const missing = REQUIRED_FIELDS.filter((field) => !fieldByColumn.includes(field));
        if (missing.length > 0) {
            throw new Error(
                `Missing required column(s): ${missing.map((f) => FIELD_LABELS[f] || f).join(', ')}. ` +
                'Download the template to see the expected format.'
            );
        }
    }

    const rows = dataRows
        .map((cells, index) => {
            const out = { __rowNumber: index + firstDataSheetRow };
            const raw = {};
            fieldByColumn.forEach((field, columnIndex) => {
                if (!field) return;
                const value = clean((cells || [])[columnIndex]);
                out[field] = value;
                raw[field] = value;
            });
            out.__raw = raw;
            return out;
        })
        // drop entirely empty rows
        .filter((row) => Object.values(row.__raw).some((value) => String(value ?? '').trim()));

    if (rows.length === 0) {
        throw new Error('No passenger rows were found.');
    }

    // Without a heading row we are trusting column order, so make sure this actually
    // looks like passenger data rather than a stray line of text.
    if (!hasHeaderRow) {
        const populated = Object.values(rows[0].__raw).filter((value) => String(value ?? '').trim()).length;
        if (populated < REQUIRED_FIELDS.length) {
            throw new Error(
                'These rows could not be recognised. Include the heading row, or copy all ' +
                `${BULK_TEMPLATE_COLUMNS.length - 1} columns in template order: ` +
                `${BULK_TEMPLATE_COLUMNS.slice(0, -1).join(', ')}.`
            );
        }
    }

    return { rows, unknownHeaders, assumedColumnOrder: !hasHeaderRow };
};

/**
 * Read a CSV or XLSX File/Blob into raw rows. Rejects with a readable message
 * when the file is unusable.
 */
export const readBookingFile = (file) =>
    new Promise((resolve, reject) => {
        if (!file) {
            reject(new Error('No file selected'));
            return;
        }

        const reader = new FileReader();

        reader.onerror = () => reject(new Error('The file could not be read. Please try again.'));

        reader.onload = (event) => {
            let workbook;

            // Reading the workbook and interpreting it are separated so an unreadable
            // file gets a plain-English message instead of the library's internals.
            try {
                workbook = XLSX.read(event.target.result, { type: 'array', cellDates: true });
            } catch (error) {
                reject(new Error(
                    'This file could not be read as a spreadsheet. Save it as CSV or Excel, or paste the rows instead.'
                ));
                return;
            }

            try {
                const sheetName = workbook.SheetNames[0];
                if (!sheetName) throw new Error('The file contains no worksheets.');
                resolve({ ...interpretSheet(workbook.Sheets[sheetName]), sheetName });
            } catch (error) {
                reject(error instanceof Error ? error : new Error('The file could not be parsed.'));
            }
        };

        reader.readAsArrayBuffer(file);
    });

/**
 * Read rows copied straight out of a spreadsheet application.
 *
 * Numbers, Excel and Google Sheets all place the selected cells on the clipboard as
 * delimited text, so this accepts data from any of them — including formats we cannot
 * open as files. The heading row is optional.
 */
export const readPastedRows = (text) => {
    const content = String(text || '').trim();

    if (!content) {
        throw new Error('Nothing was pasted. Copy the rows from your spreadsheet and paste them here.');
    }

    let workbook;
    try {
        workbook = XLSX.read(content, { type: 'string', cellDates: true });
    } catch (error) {
        throw new Error('The pasted text could not be read. Copy whole rows from your spreadsheet and try again.');
    }

    const sheetName = workbook.SheetNames[0];
    if (!sheetName) throw new Error('The pasted text contained no rows.');

    return interpretSheet(workbook.Sheets[sheetName]);
};

// ─── validation ───────────────────────────────────────────────────────────────

/**
 * Validate and normalise parsed rows.
 *
 * @param {Array}  rows      output of readBookingFile
 * @param {Object} options   { authority, isAirline, iata }
 * @returns {{ rows: Array, validCount: number, errorCount: number, flights: Array }}
 */
export const validateBookingRows = (rows, options = {}) => {
    const { authority, isAirline = false } = options;

    const allowedCodes = getPRMCodeOptions(authority).map((option) => option.value);

    const yesterday = new Date();
    yesterday.setHours(0, 0, 0, 0);
    yesterday.setDate(yesterday.getDate() - 1);

    const seen = new Map();   // duplicate detection within the file
    const flights = new Map();

    const validated = rows
        .filter((row) => !isPlaceholderRow(row.__raw || row))
        .map((row) => {
            const errors = {};
            const warnings = [];

            // names
            const firstName = cleanName(row.firstName);
            const secondName = cleanName(row.secondName);
            if (!firstName) errors.firstName = 'First name required';
            if (!secondName) errors.secondName = 'Last name required';

            // assistance codes
            const assistance = parseAssistance(row.assistance);
            if (assistance.length === 0) {
                errors.assistance = 'At least one assistance code required';
            } else {
                const unknown = assistance.filter((code) => !PRMCodes.includes(code));
                const restricted = assistance.filter(
                    (code) => PRMCodes.includes(code) && !allowedCodes.includes(code)
                );
                if (unknown.length > 0) {
                    errors.assistance = `Unrecognised code(s): ${unknown.join(', ')}`;
                } else if (restricted.length > 0) {
                    errors.assistance = `Code(s) not available for this airport: ${restricted.join(', ')}`;
                }
            }

            // origin / destination assistance — default to true, matching the single-request form
            const originAssistance = parseBoolean(row.originAssistance, true);
            const destinationAssistance = parseBoolean(row.destinationAssistance, true);
            if (originAssistance === null) errors.originAssistance = 'Use Y or N';
            if (destinationAssistance === null) errors.destinationAssistance = 'Use Y or N';
            if (originAssistance === false && destinationAssistance === false) {
                errors.originAssistance = 'Assistance must be requested at origin, destination or both';
            }

            // airline confirmation — airline accounts confirm implicitly
            let airlineConfirmed = parseBoolean(row.airlineConfirmed, false);
            if (airlineConfirmed === null) {
                errors.airlineConfirmed = 'Use Y or N';
                airlineConfirmed = false;
            }
            if (isAirline) airlineConfirmed = true;

            // flight
            const carrierCode = normaliseCarrierCode(row.carrierCode);
            if (!carrierCode || !CARRIER_CODE_REGEX.test(carrierCode)) {
                errors.carrierCode = 'Enter a valid carrier code (e.g. EI, FR, EK)';
            }

            const flightNumber = String(row.flightNumber || '').trim().replace(/^0+(?=\d)/, '').toUpperCase();
            if (!flightNumber || !FLIGHT_NUMBER_REGEX.test(flightNumber)) {
                errors.flightNumber = 'Enter a valid flight number (1–4 digits)';
            }

            const flightDate = parseFlightDate(row.flightDate);
            if (!flightDate) {
                errors.flightDate = 'Enter the flight date as dd/mm/yyyy';
            } else if (new Date(`${flightDate}T00:00:00`) < yesterday) {
                errors.flightDate = 'Flight date cannot be before yesterday';
            }

            const flightType = parseDirection(row.flightType);
            if (!flightType) errors.flightType = 'Enter Inbound or Outbound';

            const referenceNumber = String(row.referenceNumber || '').trim();

            const result = {
                __rowNumber: row.__rowNumber,
                firstName,
                secondName,
                assistance,
                originAssistance: originAssistance === null ? true : originAssistance,
                destinationAssistance: destinationAssistance === null ? true : destinationAssistance,
                airlineConfirmed,
                carrierCode,
                flightNumber,
                flightDate,
                flightType,
                referenceNumber,
                warnings,
                errors,
            };

            // duplicates within the same file
            if (Object.keys(errors).length === 0) {
                const key = `${firstName.toLowerCase()} ${secondName.toLowerCase()}|${flightKeyOf(result)}`;
                if (seen.has(key)) {
                    result.errors.duplicate = `Duplicate of row ${seen.get(key)}`;
                } else {
                    seen.set(key, row.__rowNumber);
                }
            }

            result.isValid = Object.keys(result.errors).length === 0;

            if (result.isValid) {
                const key = flightKeyOf(result);
                if (!flights.has(key)) {
                    flights.set(key, { key, label: flightLabelOf(result), count: 0 });
                }
                flights.get(key).count += 1;
            }

            return result;
        });

    return {
        rows: validated,
        validCount: validated.filter((r) => r.isValid).length,
        errorCount: validated.filter((r) => !r.isValid).length,
        flights: Array.from(flights.values()),
    };
};

// ─── booking documents ────────────────────────────────────────────────────────

/**
 * Turn a validated row into the request body accepted by POST /api/v2/bookings.
 * Mirrors createBookingDocument() in booking-form.js so bulk and single-request
 * submissions produce identical documents.
 */
export const buildBookingDocument = (row) => {
    const flightDetails = {
        carrierCode: row.carrierCode,
        flightNumber: row.flightNumber,
        ...(row.flightType === 'INBOUND'
            ? { arrivalDate: row.flightDate }
            : { departureDate: row.flightDate }),
    };

    return {
        name: `${row.firstName} ${row.secondName}`.trim(),
        flightDetails,
        assistance: row.assistance,
        referenceNumber: row.referenceNumber || '',
        destinationAssistance: row.destinationAssistance,
        originAssistance: row.originAssistance,
        airlineConfirmed: row.airlineConfirmed,
        source: { source: 'PORTAL', data: {}, bulkUpload: true },
    };
};

// ─── template / export helpers ────────────────────────────────────────────────

// The worked example shipped in both templates. The first cell marks it as guidance so
// it is dropped on upload even if the operator forgets to delete it.
const TEMPLATE_EXAMPLE_ROW = [
    'EXAMPLE - delete this row',
    'Bloggs',
    'WCHR',
    'Y',
    'Y',
    'Y',
    'EI',
    '1234',
    '21/09/2026',
    'Outbound',
    '',
];

const csvCell = (value) => {
    const text = String(sanitizeSpreadsheetCell(value ?? ''));
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/**
 * The template as a real .xlsx workbook.
 *
 * Offered alongside the CSV because a spreadsheet application round-trips an .xlsx
 * unchanged, whereas a CSV opened on a Mac is liable to be saved back out as a
 * .numbers document, which cannot be uploaded.
 */
export const buildTemplateWorkbookBlob = () => {
    const sheet = XLSX.utils.aoa_to_sheet([BULK_TEMPLATE_COLUMNS, TEMPLATE_EXAMPLE_ROW]);

    sheet['!cols'] = BULK_TEMPLATE_COLUMNS.map(() => ({ wch: 22 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Assistance Requests');

    const output = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    return new Blob([output], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
};

export const buildTemplateCsv = () =>
    [BULK_TEMPLATE_COLUMNS.map(csvCell).join(','), TEMPLATE_EXAMPLE_ROW.map(csvCell).join(',')].join('\n');

/** CSV of rows that failed, with a Reason column, so the operator can fix and re-upload. */
export const buildErrorCsv = (rows) => {
    const header = [...BULK_TEMPLATE_COLUMNS, 'Reason'].join(',');
    const lines = rows
        .filter((row) => !row.isValid)
        .map((row) =>
            [
                row.firstName,
                row.secondName,
                (row.assistance || []).join(' '),
                row.airlineConfirmed ? 'Y' : 'N',
                row.originAssistance ? 'Y' : 'N',
                row.destinationAssistance ? 'Y' : 'N',
                row.carrierCode,
                row.flightNumber,
                row.flightDate,
                row.flightType,
                row.referenceNumber,
                Object.values(row.errors || {}).join('; '),
            ]
                .map(csvCell)
                .join(',')
        );
    return [header, ...lines].join('\n');
};

export const downloadBlob = (filename, blob) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export const downloadCsv = (filename, contents) =>
    downloadBlob(filename, new Blob(['\uFEFF' + contents], { type: 'text/csv;charset=utf-8;' }));
